import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  startTestRun,
  getTestRunStatus,
  startConfigOptimization,
  getOptimizationStatus,
  getDefaultTestQueries,
  getConfigOptimizationTemplates,
  getTestHistory,
  getTestHistoryResult
} from '../services/api';
import SCGRuleLink from '../components/SCGRuleLink';
import {
  PlayIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/solid';

const FAILURE_MODE_LABELS = {
  LEVEL_INFLATION: { label: 'Over-Classified', bg: 'bg-orange-900/30', text: 'text-orange-400', bgHover: 'hover:bg-orange-900/50', bgActive: 'bg-orange-600' },
  LEVEL_DEFLATION: { label: 'Under-Classified', bg: 'bg-teal-900/30', text: 'text-teal-400', bgHover: 'hover:bg-teal-900/50', bgActive: 'bg-teal-600' },
  CONTROLS_MISMATCH: { label: 'Controls Wrong', bg: 'bg-pink-900/30', text: 'text-pink-400', bgHover: 'hover:bg-pink-900/50', bgActive: 'bg-pink-600' },
  COMPARTMENT_MISS: { label: 'Compartments Wrong', bg: 'bg-purple-900/30', text: 'text-purple-400', bgHover: 'hover:bg-purple-900/50', bgActive: 'bg-purple-600' },
  PARSE_ERROR: { label: 'Parse Error', bg: 'bg-red-900/30', text: 'text-red-400', bgHover: 'hover:bg-red-900/50', bgActive: 'bg-red-600' },
};

const ROOT_CAUSE_LABELS = {
  CORRECT: { label: 'Correct', color: '#22c55e', bg: 'rgba(34,197,94,0.15)', border: 'rgba(34,197,94,0.3)' },
  RETRIEVAL_MISS: { label: 'Retrieval Miss', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)' },
  RETRIEVAL_NOISE: { label: 'Retrieval Noise', color: '#f97316', bg: 'rgba(249,115,22,0.15)', border: 'rgba(249,115,22,0.3)' },
  LLM_OVERRIDE: { label: 'LLM Override', color: '#a855f7', bg: 'rgba(168,85,247,0.15)', border: 'rgba(168,85,247,0.3)' },
  LLM_HALLUCINATION: { label: 'LLM Hallucination', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', border: 'rgba(239,68,68,0.3)' },
  RULE_GAP: { label: 'Rule Gap', color: '#eab308', bg: 'rgba(234,179,8,0.15)', border: 'rgba(234,179,8,0.3)' },
  CONFIDENCE_LOW: { label: 'Low Confidence', color: '#9ca3af', bg: 'rgba(156,163,175,0.15)', border: 'rgba(156,163,175,0.3)' },
};

/** Compute root cause distribution counts from result array */
const computeRootCauseCounts = (results) => {
  const counts = {};
  if (!results) return counts;
  results.forEach(r => {
    const primary = r.root_cause?.primary_cause;
    if (primary) {
      counts[primary] = (counts[primary] || 0) + 1;
    }
  });
  return counts;
};

const Testing = () => {
  const [activeTab, setActiveTab] = useState('test-runner');
  const [selectedTestType, setSelectedTestType] = useState('classification');
  const [selectedTemplate, setSelectedTemplate] = useState('balanced_optimization');
  const [customQueries, setCustomQueries] = useState([]);
  const [runningTestId, setRunningTestId] = useState(null);
  const [runningOptimizationId, setRunningOptimizationId] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [failureModeFilter, setFailureModeFilter] = useState(null);
  const [rootCauseFilter, setRootCauseFilter] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loadingHistoryFile, setLoadingHistoryFile] = useState(null);
  const [retrievalMode, setRetrievalMode] = useState('classic');

  const queryClient = useQueryClient();

  // Load default test queries
  const { data: defaultQueries } = useQuery({
    queryKey: ['defaultTestQueries'],
    queryFn: getDefaultTestQueries,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Load optimization templates
  const { data: optimizationTemplates } = useQuery({
    queryKey: ['optimizationTemplates'],
    queryFn: getConfigOptimizationTemplates,
    staleTime: 10 * 60 * 1000 // 10 minutes
  });

  // Poll test run status
  const { data: testRunStatus } = useQuery({
    queryKey: ['testRunStatus', runningTestId],
    queryFn: () => getTestRunStatus(runningTestId),
    enabled: !!runningTestId,
    refetchInterval: 2000,
  });

  // When test run completes, store results and stop polling
  useEffect(() => {
    if (testRunStatus && (testRunStatus.status === 'completed' || testRunStatus.status === 'failed')) {
      setTestResults(testRunStatus);
      setRunningTestId(null);
    }
  }, [testRunStatus]);

  // Poll optimization status
  const { data: optimizationStatus } = useQuery({
    queryKey: ['optimizationStatus', runningOptimizationId],
    queryFn: () => getOptimizationStatus(runningOptimizationId),
    enabled: !!runningOptimizationId,
    refetchInterval: 3000,
  });

  // When optimization completes, store results and stop polling
  useEffect(() => {
    if (optimizationStatus && (optimizationStatus.status === 'completed' || optimizationStatus.status === 'failed')) {
      setOptimizationResults(optimizationStatus);
      setRunningOptimizationId(null);
    }
  }, [optimizationStatus]);

  // Fetch test history list
  const { data: testHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['testHistory'],
    queryFn: getTestHistory,
    staleTime: 30 * 1000,
    enabled: activeTab === 'history',
  });

  const handleLoadHistoryResult = async (filename) => {
    setLoadingHistoryFile(filename);
    try {
      const result = await getTestHistoryResult(filename);
      setTestResults(result);
      setFailureModeFilter(null);
      setRootCauseFilter(null);
      setExpandedRows(new Set());
      setActiveTab('test-runner');
    } catch (err) {
      console.error('Failed to load history result:', err);
    } finally {
      setLoadingHistoryFile(null);
    }
  };

  // Start test run mutation
  const startTestMutation = useMutation({
    mutationFn: ({ testType, testQueries, configParams, retrievalMode }) =>
      startTestRun(testType, testQueries, configParams, retrievalMode),
    onSuccess: (data) => {
      setRunningTestId(data.test_id);
      setTestResults(null);
      setFailureModeFilter(null);
      setRootCauseFilter(null);
      setExpandedRows(new Set());
    }
  });

  // Start optimization mutation
  const startOptimizationMutation = useMutation({
    mutationFn: ({ optimizationParams, testQueries, scoringWeights }) => 
      startConfigOptimization(optimizationParams, testQueries, scoringWeights),
    onSuccess: (data) => {
      setRunningOptimizationId(data.optimization_id);
      setOptimizationResults(null);
    }
  });

  const handleStartTest = () => {
    const queries = customQueries.length > 0 ? customQueries : null;
    startTestMutation.mutate({
      testType: selectedTestType,
      testQueries: queries,
      configParams: null,
      retrievalMode: retrievalMode
    });
  };

  const handleLoadDefaults = () => {
    if (!defaultQueries?.queries) return;
    const loaded = defaultQueries.queries.map((q, i) => ({
      id: `default_${Date.now()}_${i}`,
      text: q.text,
      expected_classification: q.expected_classification || 'UNCLASSIFIED',
      expected_reasoning: '',
      expected_controls: [],
      expected_compartments: [],
      category: q.category || ''
    }));
    setCustomQueries(loaded);
  };

  const handleStartOptimization = () => {
    const template = optimizationTemplates?.[selectedTemplate];
    if (!template) return;

    const queries = customQueries.length > 0 ? customQueries : null;
    startOptimizationMutation.mutate({
      optimizationParams: template.optimization_params,
      testQueries: queries,
      scoringWeights: template.scoring_weights
    });
  };

  const addCustomQuery = () => {
    setCustomQueries([...customQueries, {
      id: `custom_${Date.now()}`,
      text: '',
      expected_classification: 'UNCLASSIFIED',
      expected_reasoning: '',
      expected_controls: [],
      expected_compartments: []
    }]);
  };

  const updateCustomQuery = (index, field, value) => {
    const updated = [...customQueries];
    updated[index][field] = value;
    setCustomQueries(updated);
  };

  const removeCustomQuery = (index) => {
    setCustomQueries(customQueries.filter((_, i) => i !== index));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'running':
        return <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-[var(--text-muted)]" />;
    }
  };

  const getGrade = (score) => {
    if (score >= 0.9) return 'A';
    if (score >= 0.75) return 'B';
    if (score >= 0.6) return 'C';
    return 'D';
  };

  const toggleRowExpansion = (queryId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(queryId)) {
        next.delete(queryId);
      } else {
        next.add(queryId);
      }
      return next;
    });
  };

  /** Render an inline root cause badge with color coding */
  const renderRootCauseBadge = (rootCause) => {
    if (!rootCause?.primary_cause) return null;
    const meta = ROOT_CAUSE_LABELS[rootCause.primary_cause] || ROOT_CAUSE_LABELS.CONFIDENCE_LOW;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 8px',
          borderRadius: '9999px',
          fontSize: '11px',
          fontWeight: 600,
          color: meta.color,
          backgroundColor: meta.bg,
          border: `1px solid ${meta.border}`,
          whiteSpace: 'nowrap',
        }}
        title={rootCause.explanation || ''}
      >
        {meta.label}
      </span>
    );
  };

  /** Render the expandable detail row for a result */
  const renderExpandedDetail = (result) => {
    const rootCause = result.root_cause || {};
    const citedRules = result.cited_rules || [];
    const expectedRules = result.expected_rules || [];
    const retrievalResults = result.retrieval_results || [];
    const llmReasoning = result.llm_reasoning || '';

    // Compute rule sets
    const citedSet = new Set(citedRules);
    const expectedSet = new Set(expectedRules);
    const retrievedIds = new Set(retrievalResults.map(r => r.id || r.rule_id));

    // Categorize rules for expected vs cited comparison
    const matchedRules = expectedRules.filter(r => citedSet.has(r));
    const missedRules = expectedRules.filter(r => !citedSet.has(r));
    const unexpectedRules = citedRules.filter(r => !expectedSet.has(r));

    const hasRuleData = expectedRules.length > 0 || citedRules.length > 0;
    const hasRetrievalData = retrievalResults.length > 0;
    const hasRootCause = !!rootCause.primary_cause;

    return (
      <tr>
        <td colSpan={9} style={{ padding: 0 }}>
          <div style={{
            backgroundColor: 'var(--vault-bg)',
            borderTop: '1px solid var(--vault-border)',
            borderBottom: '2px solid var(--vault-border)',
            padding: '16px 24px',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: hasRetrievalData ? '1fr 1fr' : '1fr', gap: '16px' }}>
              {/* Left Column: Root Cause + Rules Comparison */}
              <div>
                {/* Root Cause Summary */}
                {hasRootCause && (
                  <div style={{
                    backgroundColor: 'var(--vault-surface)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    border: `1px solid ${(ROOT_CAUSE_LABELS[rootCause.primary_cause] || ROOT_CAUSE_LABELS.CONFIDENCE_LOW).border}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <MagnifyingGlassIcon style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }} />
                      <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
                        Root Cause
                      </span>
                      {renderRootCauseBadge(rootCause)}
                      {rootCause.secondary_labels?.length > 0 && rootCause.secondary_labels.map((sl, i) => {
                        const slMeta = ROOT_CAUSE_LABELS[sl] || ROOT_CAUSE_LABELS.CONFIDENCE_LOW;
                        return (
                          <span key={i} style={{
                            fontSize: '10px', fontWeight: 500, color: slMeta.color, opacity: 0.7,
                          }}>
                            +{slMeta.label}
                          </span>
                        );
                      })}
                    </div>
                    {rootCause.explanation && (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {rootCause.explanation}
                      </div>
                    )}
                    {rootCause.evidence && (
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', fontStyle: 'italic' }}>
                        {typeof rootCause.evidence === 'string' ? rootCause.evidence : JSON.stringify(rootCause.evidence, null, 2)}
                      </div>
                    )}
                  </div>
                )}

                {/* Expected vs Cited Rules */}
                {hasRuleData && (
                  <div style={{
                    backgroundColor: 'var(--vault-surface)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    border: '1px solid var(--vault-border)',
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                      Expected vs Cited Rules
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                      {/* Matched (green) */}
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#22c55e', marginBottom: '4px' }}>
                          Matched ({matchedRules.length})
                        </div>
                        {matchedRules.length > 0 ? matchedRules.map((r, i) => (
                          <div key={i} style={{
                            fontSize: '11px', padding: '2px 6px', borderRadius: '4px', marginBottom: '2px',
                            backgroundColor: 'rgba(34,197,94,0.12)', color: '#4ade80', fontFamily: 'monospace',
                          }}>
                            <SCGRuleLink ruleId={r} />
                          </div>
                        )) : (
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>None</div>
                        )}
                      </div>
                      {/* Missed (red) — in expected but not cited */}
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#ef4444', marginBottom: '4px' }}>
                          Missed ({missedRules.length})
                        </div>
                        {missedRules.length > 0 ? missedRules.map((r, i) => (
                          <div key={i} style={{
                            fontSize: '11px', padding: '2px 6px', borderRadius: '4px', marginBottom: '2px',
                            backgroundColor: 'rgba(239,68,68,0.12)', color: '#f87171', fontFamily: 'monospace',
                          }}>
                            <SCGRuleLink ruleId={r} />
                            {/* Indicate if it was at least retrieved */}
                            {retrievedIds.has(r) && (
                              <span style={{ fontSize: '9px', color: '#f97316', marginLeft: '4px' }} title="Was retrieved but not cited">
                                (retrieved)
                              </span>
                            )}
                          </div>
                        )) : (
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>None</div>
                        )}
                      </div>
                      {/* Unexpected (orange) — cited but not expected */}
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 600, color: '#f97316', marginBottom: '4px' }}>
                          Unexpected ({unexpectedRules.length})
                        </div>
                        {unexpectedRules.length > 0 ? unexpectedRules.map((r, i) => (
                          <div key={i} style={{
                            fontSize: '11px', padding: '2px 6px', borderRadius: '4px', marginBottom: '2px',
                            backgroundColor: 'rgba(249,115,22,0.12)', color: '#fb923c', fontFamily: 'monospace',
                          }}>
                            <SCGRuleLink ruleId={r} />
                          </div>
                        )) : (
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>None</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* LLM Reasoning */}
                {llmReasoning && (
                  <div style={{
                    backgroundColor: 'var(--vault-surface)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    border: '1px solid var(--vault-border)',
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '6px' }}>
                      LLM Reasoning
                    </div>
                    <div style={{
                      fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6,
                      maxHeight: '120px', overflowY: 'auto', fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {llmReasoning}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Retrieved Rules */}
              {hasRetrievalData && (
                <div style={{
                  backgroundColor: 'var(--vault-surface)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  border: '1px solid var(--vault-border)',
                  maxHeight: '400px',
                  overflowY: 'auto',
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '8px' }}>
                    Retrieved Rules ({retrievalResults.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {retrievalResults.map((rule, i) => {
                      const ruleId = rule.id || rule.rule_id || `rule-${i}`;
                      const isExpected = expectedSet.has(ruleId);
                      const isCited = citedSet.has(ruleId);
                      let borderColor = 'var(--vault-border)';
                      let bgColor = 'transparent';
                      if (isExpected && isCited) { borderColor = 'rgba(34,197,94,0.4)'; bgColor = 'rgba(34,197,94,0.06)'; }
                      else if (isExpected && !isCited) { borderColor = 'rgba(239,68,68,0.4)'; bgColor = 'rgba(239,68,68,0.06)'; }
                      else if (isCited) { borderColor = 'rgba(249,115,22,0.4)'; bgColor = 'rgba(249,115,22,0.06)'; }

                      return (
                        <div key={i} style={{
                          padding: '6px 10px', borderRadius: '6px', border: `1px solid ${borderColor}`,
                          backgroundColor: bgColor, fontSize: '11px',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--text-primary)' }}>
                              <SCGRuleLink ruleId={ruleId} />
                            </span>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              {rule.semantic_similarity != null && (
                                <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                  sim: {(rule.semantic_similarity * 100).toFixed(0)}%
                                </span>
                              )}
                              {rule.classification_level && (
                                <span style={{
                                  fontSize: '10px', fontWeight: 600, padding: '1px 5px', borderRadius: '3px',
                                  backgroundColor: 'rgba(99,102,241,0.15)', color: '#818cf8',
                                }}>
                                  {rule.classification_level}
                                </span>
                              )}
                            </div>
                          </div>
                          {rule.source_guide && (
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                              {rule.source_guide}
                            </div>
                          )}
                          {rule.item && (
                            <div style={{
                              fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                            }} title={rule.item}>
                              {rule.item}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* No diagnostic data fallback */}
            {!hasRootCause && !hasRuleData && !hasRetrievalData && !llmReasoning && (
              <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic' }}>
                No diagnostic data available for this result. Run a new test to generate root cause analysis.
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const renderTestResults = () => {
    if (!testResults?.results) return null;

    const { classification } = testResults.results;
    if (!classification) return null;

    const { summary, results } = classification;
    const levels = ['UNCLASSIFIED', 'CONFIDENTIAL', 'SECRET', 'TOP SECRET'];

    // Compute root cause distribution from results
    const rootCauseCounts = computeRootCauseCounts(results);
    const hasRootCauseData = Object.keys(rootCauseCounts).length > 0;

    // Apply both filters
    const filteredResults = results.filter(result => {
      // Failure mode filter
      if (failureModeFilter) {
        if (failureModeFilter === 'PASSED' && result.failure_mode) return false;
        if (failureModeFilter !== 'PASSED' && result.failure_mode !== failureModeFilter) return false;
      }
      // Root cause filter
      if (rootCauseFilter) {
        const primary = result.root_cause?.primary_cause;
        if (rootCauseFilter === 'NO_DATA') {
          if (primary) return false;
        } else {
          if (primary !== rootCauseFilter) return false;
        }
      }
      return true;
    });

    return (
      <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-6 mt-6">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center">
          <ChartBarIcon className="w-5 h-5 mr-2" />
          Test Results
        </h3>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-900/20 rounded-lg p-4">
            <div className="text-sm font-medium text-blue-400">Total Tests</div>
            <div className="text-2xl font-bold text-blue-300">{summary.total_tests}</div>
          </div>
          <div className="bg-green-900/20 rounded-lg p-4">
            <div className="text-sm font-medium text-green-400">Passed</div>
            <div className="text-2xl font-bold text-green-300">{summary.passed}</div>
            <div className="text-xs text-green-400">
              {summary.total_tests > 0 ? ((summary.passed / summary.total_tests) * 100).toFixed(1) : 0}%
            </div>
          </div>
          <div className="bg-red-900/20 rounded-lg p-4">
            <div className="text-sm font-medium text-red-400">Failed</div>
            <div className="text-2xl font-bold text-red-300">{summary.failed}</div>
            <div className="text-xs text-red-400">
              {summary.total_tests > 0 ? ((summary.failed / summary.total_tests) * 100).toFixed(1) : 0}%
            </div>
          </div>
          <div className="bg-yellow-900/20 rounded-lg p-4">
            <div className="text-sm font-medium text-yellow-400">Avg Response</div>
            <div className="text-2xl font-bold text-yellow-300">
              {(summary.avg_response_time_ms / 1000).toFixed(2)}s
            </div>
          </div>
        </div>

        {/* Accuracy Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-indigo-900/20 rounded-lg p-4">
            <div className="text-sm font-medium text-indigo-400">Overall Accuracy</div>
            <div className="text-xl font-bold text-indigo-300">
              {(summary.overall_accuracy * 100).toFixed(1)}%
            </div>
          </div>
          <div className="bg-purple-900/20 rounded-lg p-4">
            <div className="text-sm font-medium text-purple-400">Level Accuracy</div>
            <div className="text-xl font-bold text-purple-300">
              {(summary.level_accuracy * 100).toFixed(1)}%
            </div>
          </div>
          <div className="bg-pink-900/20 rounded-lg p-4">
            <div className="text-sm font-medium text-pink-400">Controls Accuracy</div>
            <div className="text-xl font-bold text-pink-300">
              {(summary.controls_accuracy * 100).toFixed(1)}%
            </div>
          </div>
          <div className="bg-cyan-900/20 rounded-lg p-4">
            <div className="text-sm font-medium text-cyan-400">Compartments</div>
            <div className="text-xl font-bold text-cyan-300">
              {(summary.compartments_accuracy * 100).toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Over/Under Classification */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-orange-900/20 rounded-lg p-4 border border-orange-800/30">
            <div className="text-sm font-medium text-orange-400">Over-Classification</div>
            <div className="text-xl font-bold text-orange-300">
              {summary.over_classification_count} ({(summary.over_classification_rate * 100).toFixed(1)}%)
            </div>
            <div className="text-xs text-orange-400/70 mt-1">Higher level than expected</div>
          </div>
          <div className="bg-teal-900/20 rounded-lg p-4 border border-teal-800/30">
            <div className="text-sm font-medium text-teal-400">Under-Classification</div>
            <div className="text-xl font-bold text-teal-300">
              {summary.under_classification_count} ({(summary.under_classification_rate * 100).toFixed(1)}%)
            </div>
            <div className="text-xs text-teal-400/70 mt-1">Lower level than expected</div>
          </div>
        </div>

        {/* Root Cause Distribution Summary */}
        {hasRootCauseData && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-[var(--text-secondary)] mb-3 uppercase tracking-wider">Root Cause Distribution</h4>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {/* All pill */}
              <button
                onClick={() => setRootCauseFilter(null)}
                style={{
                  display: 'inline-flex', alignItems: 'center', padding: '4px 12px',
                  borderRadius: '9999px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  border: 'none', transition: 'all 0.15s',
                  backgroundColor: rootCauseFilter === null ? '#2563eb' : 'var(--vault-elevated)',
                  color: rootCauseFilter === null ? 'white' : 'var(--text-muted)',
                }}
              >
                All ({results.length})
              </button>
              {Object.entries(rootCauseCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([cause, count]) => {
                  const meta = ROOT_CAUSE_LABELS[cause] || ROOT_CAUSE_LABELS.CONFIDENCE_LOW;
                  const isActive = rootCauseFilter === cause;
                  return (
                    <button
                      key={cause}
                      onClick={() => setRootCauseFilter(isActive ? null : cause)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', padding: '4px 12px',
                        borderRadius: '9999px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        transition: 'all 0.15s',
                        border: isActive ? `2px solid ${meta.color}` : `1px solid ${meta.border}`,
                        backgroundColor: isActive ? meta.bg : meta.bg,
                        color: meta.color,
                        opacity: isActive ? 1 : 0.85,
                      }}
                    >
                      {meta.label}: {count}
                    </button>
                  );
                })}
            </div>
            {/* Horizontal stacked bar for quick visual */}
            <div style={{
              display: 'flex', height: '8px', borderRadius: '4px', overflow: 'hidden',
              backgroundColor: 'var(--vault-elevated)',
            }}>
              {Object.entries(rootCauseCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([cause, count]) => {
                  const meta = ROOT_CAUSE_LABELS[cause] || ROOT_CAUSE_LABELS.CONFIDENCE_LOW;
                  const pct = (count / results.length) * 100;
                  return (
                    <div
                      key={cause}
                      title={`${meta.label}: ${count} (${pct.toFixed(0)}%)`}
                      style={{
                        width: `${pct}%`,
                        backgroundColor: meta.color,
                        opacity: 0.7,
                        minWidth: count > 0 ? '3px' : 0,
                      }}
                    />
                  );
                })}
            </div>
          </div>
        )}

        {/* Failure Mode Distribution */}
        {summary.failure_modes && Object.keys(summary.failure_modes).length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-[var(--text-secondary)] mb-3 uppercase tracking-wider">Failure Mode Distribution</h4>
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                onClick={() => setFailureModeFilter(null)}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  failureModeFilter === null
                    ? 'bg-blue-600 text-white'
                    : 'bg-[var(--vault-elevated)] text-[var(--text-muted)] hover:bg-[var(--vault-border)]'
                }`}
              >
                All ({summary.total_tests})
              </button>
              {Object.entries(summary.failure_modes).map(([mode, count]) => {
                const meta = FAILURE_MODE_LABELS[mode] || { label: mode, bg: 'bg-gray-900/30', text: 'text-gray-400', bgHover: 'hover:bg-gray-900/50', bgActive: 'bg-gray-600' };
                const isActive = failureModeFilter === mode;
                return (
                  <button
                    key={mode}
                    onClick={() => setFailureModeFilter(isActive ? null : mode)}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      isActive
                        ? `${meta.bgActive} text-white`
                        : `${meta.bg} ${meta.text} ${meta.bgHover}`
                    }`}
                  >
                    {meta.label}: {count}
                  </button>
                );
              })}
              <button
                onClick={() => setFailureModeFilter('PASSED')}
                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  failureModeFilter === 'PASSED'
                    ? 'bg-green-600 text-white'
                    : 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                }`}
              >
                Passed: {summary.passed}
              </button>
            </div>
          </div>
        )}

        {/* Per-Category Accuracy */}
        {summary.per_category_accuracy && Object.keys(summary.per_category_accuracy).length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-[var(--text-secondary)] mb-3 uppercase tracking-wider">Per-Category Accuracy</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(summary.per_category_accuracy).map(([cat, acc]) => (
                <div key={cat} className="bg-[var(--vault-elevated)] rounded-lg p-3">
                  <div className="text-xs text-[var(--text-muted)] truncate" title={cat}>
                    {cat.replace(/_/g, ' ')}
                  </div>
                  <div className={`text-lg font-bold ${
                    acc >= 0.8 ? 'text-green-400' :
                    acc >= 0.6 ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {(acc * 100).toFixed(0)}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confusion Matrix */}
        {summary.confusion_matrix && Object.keys(summary.confusion_matrix).length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-[var(--text-secondary)] mb-3 uppercase tracking-wider">Confusion Matrix (Actual vs Expected)</h4>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="p-2 text-left text-[var(--text-muted)] border border-[var(--vault-border)]">Actual \ Expected</th>
                    {levels.map(l => (
                      <th key={l} className="p-2 text-center text-[var(--text-muted)] border border-[var(--vault-border)] min-w-[80px]">
                        {l === 'TOP SECRET' ? 'TS' : l === 'UNCLASSIFIED' ? 'UNCLAS' : l.substring(0, 6)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {levels.map(actual => (
                    <tr key={actual}>
                      <td className="p-2 font-medium text-[var(--text-secondary)] border border-[var(--vault-border)]">
                        {actual === 'TOP SECRET' ? 'TS' : actual === 'UNCLASSIFIED' ? 'UNCLAS' : actual.substring(0, 6)}
                      </td>
                      {levels.map(expected => {
                        const count = summary.confusion_matrix?.[actual]?.[expected] || 0;
                        const isDiagonal = actual === expected;
                        return (
                          <td key={expected} className={`p-2 text-center border border-[var(--vault-border)] font-mono ${
                            isDiagonal && count > 0 ? 'bg-green-900/40 text-green-300 font-bold' :
                            !isDiagonal && count > 0 ? 'bg-red-900/30 text-red-400' :
                            'text-[var(--text-muted)]'
                          }`}>
                            {count}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detailed Results Table */}
        <h4 className="text-sm font-semibold text-[var(--text-secondary)] mb-3 uppercase tracking-wider">
          Detailed Results
          {(failureModeFilter || rootCauseFilter) && (
            <span className="ml-2 text-xs font-normal text-[var(--text-muted)]">
              (filtered{failureModeFilter ? `: ${failureModeFilter === 'PASSED' ? 'Passed' : (FAILURE_MODE_LABELS[failureModeFilter]?.label || failureModeFilter)}` : ''}
              {rootCauseFilter ? `${failureModeFilter ? ' + ' : ': '}${(ROOT_CAUSE_LABELS[rootCauseFilter]?.label || rootCauseFilter)}` : ''})
            </span>
          )}
          <span className="ml-2 text-xs font-normal text-[var(--text-muted)]">
            {filteredResults.length} of {results.length}
          </span>
        </h4>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[var(--vault-border)]">
            <thead className="bg-[var(--vault-elevated)]">
              <tr>
                <th className="px-2 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider" style={{ width: '28px' }}></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Expected</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Actual</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Diagnosis</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Root Cause</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">Time</th>
              </tr>
            </thead>
            <tbody className="bg-[var(--vault-surface)] divide-y divide-[var(--vault-border)]">
              {filteredResults.map((result, index) => {
                const failMeta = result.failure_mode
                  ? (FAILURE_MODE_LABELS[result.failure_mode] || { label: result.failure_mode, bg: 'bg-gray-900/30', text: 'text-gray-400' })
                  : null;
                const isExpanded = expandedRows.has(result.query_id);
                const hasDetailData = result.root_cause?.primary_cause || (result.cited_rules?.length > 0) || (result.retrieval_results?.length > 0) || result.llm_reasoning;

                return (
                  <React.Fragment key={result.query_id || index}>
                    <tr
                      className={index % 2 === 0 ? 'bg-[var(--vault-surface)]' : 'bg-[var(--vault-elevated)]'}
                      style={{ cursor: hasDetailData ? 'pointer' : 'default' }}
                      onClick={() => hasDetailData && toggleRowExpansion(result.query_id)}
                    >
                      <td className="px-2 py-3 text-center" style={{ width: '28px' }}>
                        {hasDetailData ? (
                          isExpanded
                            ? <ChevronDownIcon style={{ width: '14px', height: '14px', color: 'var(--spotlight-gold)' }} />
                            : <ChevronRightIcon style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }} />
                        ) : null}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-[var(--text-primary)]">
                        {result.query_id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-[var(--text-muted)]">
                        {result.category?.replace(/_/g, ' ')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--text-muted)]">
                        {result.expected_level}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={result.level_match ? 'text-green-400' : 'text-red-400'}>
                          {result.full_classification || result.actual_level}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {failMeta ? (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${failMeta.bg} ${failMeta.text}`}>
                            {failMeta.label}
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-900/30 text-green-400">
                            Pass
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {renderRootCauseBadge(result.root_cause)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          result.overall_score >= 0.8 ? 'bg-green-900/30 text-green-400' :
                          result.overall_score >= 0.6 ? 'bg-yellow-900/30 text-yellow-400' :
                          'bg-red-900/30 text-red-400'
                        }`}>
                          {(result.overall_score * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-[var(--text-muted)]">
                        {(result.response_time_ms / 1000).toFixed(2)}s
                      </td>
                    </tr>
                    {isExpanded && renderExpandedDetail(result)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderOptimizationResults = () => {
    if (!optimizationResults?.results) return null;

    const { config_runs, best_config } = optimizationResults.results;
    if (!config_runs || !best_config) return null;

    return (
      <div className="bg-[var(--vault-surface)] rounded-lg border border-[var(--vault-border)] p-6 mt-6">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center">
          <AdjustmentsHorizontalIcon className="w-5 h-5 mr-2" />
          Optimization Results
        </h3>

        {/* Best Configuration */}
        <div className="bg-green-900/20 rounded-lg p-6 border border-green-800 mb-6">
          <h4 className="text-md font-semibold text-green-300 mb-3">
            Best Configuration (Score: {best_config.optimization_score.toFixed(3)})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="text-sm font-medium text-green-400 mb-2">Parameters:</h5>
              <pre className="text-xs bg-[var(--vault-bg)] rounded p-2 text-green-400">
                {JSON.stringify(best_config.config_params, null, 2)}
              </pre>
            </div>
            <div>
              <h5 className="text-sm font-medium text-green-400 mb-2">Performance:</h5>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Overall Accuracy: {(best_config.test_summary.overall_accuracy * 100).toFixed(1)}%</div>
                <div>Classification: {(best_config.test_summary.classification_accuracy * 100).toFixed(1)}%</div>
                <div>Avg Response: {best_config.test_summary.average_response_time.toFixed(2)}s</div>
                <div>Confidence: {(best_config.test_summary.average_confidence * 100).toFixed(1)}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* All Configuration Results */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[var(--vault-border)]">
            <thead className="bg-[var(--vault-elevated)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Config Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Optimization Score
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Overall Accuracy
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Avg Response Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Passed Tests
                </th>
              </tr>
            </thead>
            <tbody className="bg-[var(--vault-surface)] divide-y divide-[var(--vault-border)]">
              {config_runs
                .sort((a, b) => b.optimization_score - a.optimization_score)
                .map((run, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-[var(--vault-surface)]' : 'bg-[var(--vault-elevated)]'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--text-primary)]">
                    {run.config_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-muted)]">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      run.optimization_score >= 0.8 ? 'bg-green-900/30 text-green-400' :
                      run.optimization_score >= 0.6 ? 'bg-yellow-900/30 text-yellow-400' :
                      'bg-red-900/30 text-red-400'
                    }`}>
                      {run.optimization_score.toFixed(3)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-muted)]">
                    {(run.test_summary.overall_accuracy * 100).toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-muted)]">
                    {run.test_summary.average_response_time.toFixed(2)}s
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-muted)]">
                    {run.test_summary.passed_tests}/{run.test_summary.total_tests}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[var(--vault-surface)] border border-[var(--vault-border)] rounded-lg">
          {/* Header */}
          <div className="px-6 py-4 border-b border-[var(--vault-border)]">
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">System Testing & Optimization</h1>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Run comprehensive tests and optimize system configuration for peak performance
            </p>
          </div>

          {/* Tabs */}
          <div className="border-b border-[var(--vault-border)]">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('test-runner')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'test-runner'
                    ? 'border-[var(--spotlight-gold)] text-[var(--spotlight-gold)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--vault-border)]'
                }`}
              >
                <PlayIcon className="w-5 h-5 inline mr-2" />
                Test Runner
              </button>
              <button
                onClick={() => setActiveTab('config-optimizer')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'config-optimizer'
                    ? 'border-[var(--spotlight-gold)] text-[var(--spotlight-gold)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--vault-border)]'
                }`}
              >
                <Cog6ToothIcon className="w-5 h-5 inline mr-2" />
                Config Optimizer
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'history'
                    ? 'border-[var(--spotlight-gold)] text-[var(--spotlight-gold)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--vault-border)]'
                }`}
              >
                <ClipboardDocumentListIcon className="w-5 h-5 inline mr-2" />
                History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'test-runner' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Test Configuration */}
                  <div className="lg:col-span-1">
                    <div className="bg-[var(--vault-elevated)] rounded-lg p-4">
                      <h3 className="text-lg font-medium text-[var(--text-primary)] mb-4">Test Configuration</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                            Test Type
                          </label>
                          <select
                            value={selectedTestType}
                            onChange={(e) => setSelectedTestType(e.target.value)}
                            className="block w-full rounded-md border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] shadow-sm focus:border-[var(--spotlight-gold)] focus:ring-[var(--spotlight-gold)]"
                          >
                            <option value="classification">Classification Tests</option>
                            <option value="sanitization">Sanitization Tests</option>
                            <option value="both">Both</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                            Test Queries
                          </label>
                          <div className="text-sm text-[var(--text-muted)] mb-2">
                            {defaultQueries?.test_queries ? 
                              `${Object.values(defaultQueries.test_queries).flat().length} default queries available` :
                              'Loading default queries...'
                            }
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={addCustomQuery}
                              className="flex-1 py-2 px-4 border border-[var(--vault-border)] rounded-md text-sm font-medium text-[var(--text-secondary)] bg-[var(--vault-surface)] hover:bg-[var(--vault-elevated)]"
                            >
                              Add Custom Query
                            </button>
                            <button
                              onClick={handleLoadDefaults}
                              disabled={!defaultQueries?.queries}
                              className="flex-1 py-2 px-4 border border-blue-700 rounded-md text-sm font-medium text-blue-400 bg-[var(--vault-surface)] hover:bg-blue-900/30 disabled:opacity-50"
                            >
                              Load Defaults
                            </button>
                          </div>
                          {customQueries.length > 0 && (
                            <div className="text-xs text-[var(--text-muted)] mt-1">
                              {customQueries.length} custom {customQueries.length === 1 ? 'query' : 'queries'} loaded
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                            Retrieval Mode
                          </label>
                          <select
                            value={retrievalMode}
                            onChange={(e) => setRetrievalMode(e.target.value)}
                            className="block w-full rounded-md border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] shadow-sm focus:border-[var(--spotlight-gold)] focus:ring-[var(--spotlight-gold)]"
                          >
                            <option value="classic">Classic (fastest)</option>
                            <option value="enhanced">Enhanced (default)</option>
                            <option value="agentic">Agentic (best recall)</option>
                          </select>
                        </div>

                        <button
                          onClick={handleStartTest}
                          disabled={startTestMutation.isLoading || !!runningTestId}
                          className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-[var(--spotlight-gold)] hover:opacity-90 disabled:opacity-50"
                        >
                          {runningTestId ? (
                            <>
                              <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                              Running Tests...
                            </>
                          ) : (
                            <>
                              <PlayIcon className="w-4 h-4 mr-2" />
                              Start Test Run
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Test Status */}
                    {(runningTestId || testResults) && (
                      <div className="mt-6 bg-[var(--vault-elevated)] rounded-lg p-4">
                        <h3 className="text-lg font-medium text-[var(--text-primary)] mb-4">Test Status</h3>
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(testRunStatus?.status || testResults?.status)}
                          <div>
                            <div className="text-sm font-medium text-[var(--text-primary)]">
                              {testRunStatus?.status || testResults?.status || 'Unknown'}
                            </div>
                            {testRunStatus?.progress?.stage && (
                              <div className="text-xs text-[var(--text-muted)]">
                                {testRunStatus.progress.stage}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Custom Queries */}
                  <div className="lg:col-span-2">
                    {customQueries.length > 0 && (
                      <div className="bg-[var(--vault-elevated)] rounded-lg p-4">
                        <h3 className="text-lg font-medium text-[var(--text-primary)] mb-4">Custom Test Queries</h3>
                        <div className="space-y-4">
                          {customQueries.map((query, index) => (
                            <div key={query.id} className="bg-[var(--vault-surface)] rounded-lg p-4 border border-[var(--vault-border)]">
                              <div className="flex justify-between items-start mb-3">
                                <h4 className="text-sm font-medium text-[var(--text-primary)]">Query {index + 1}</h4>
                                <button
                                  onClick={() => removeCustomQuery(index)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <XCircleIcon className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="grid grid-cols-1 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                                    Text
                                  </label>
                                  <textarea
                                    value={query.text}
                                    onChange={(e) => updateCustomQuery(index, 'text', e.target.value)}
                                    className="block w-full rounded-md border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] shadow-sm focus:border-[var(--spotlight-gold)] focus:ring-[var(--spotlight-gold)] text-sm placeholder-[var(--text-muted)]"
                                    rows={2}
                                    placeholder="Enter test query text..."
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                                      Expected Classification
                                    </label>
                                    <select
                                      value={query.expected_classification}
                                      onChange={(e) => updateCustomQuery(index, 'expected_classification', e.target.value)}
                                      className="block w-full rounded-md border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] shadow-sm focus:border-[var(--spotlight-gold)] focus:ring-[var(--spotlight-gold)] text-sm"
                                    >
                                      <option value="UNCLASSIFIED">UNCLASSIFIED</option>
                                      <option value="CONFIDENTIAL">CONFIDENTIAL</option>
                                      <option value="SECRET">SECRET</option>
                                      <option value="TOP SECRET">TOP SECRET</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
                                      Expected Reasoning
                                    </label>
                                    <input
                                      type="text"
                                      value={query.expected_reasoning}
                                      onChange={(e) => updateCustomQuery(index, 'expected_reasoning', e.target.value)}
                                      className="block w-full rounded-md border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] shadow-sm focus:border-[var(--spotlight-gold)] focus:ring-[var(--spotlight-gold)] text-sm placeholder-[var(--text-muted)]"
                                      placeholder="Brief reasoning..."
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Test Results */}
                {renderTestResults()}
              </div>
            )}

            {activeTab === 'config-optimizer' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Optimization Configuration */}
                  <div className="lg:col-span-1">
                    <div className="bg-[var(--vault-elevated)] rounded-lg p-4">
                      <h3 className="text-lg font-medium text-[var(--text-primary)] mb-4">Optimization Configuration</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                            Optimization Template
                          </label>
                          <select
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            className="block w-full rounded-md border-[var(--vault-border)] bg-[var(--vault-bg)] text-[var(--text-primary)] shadow-sm focus:border-[var(--spotlight-gold)] focus:ring-[var(--spotlight-gold)]"
                          >
                            {optimizationTemplates && Object.entries(optimizationTemplates).map(([key, template]) => (
                              <option key={key} value={key}>
                                {template.description}
                              </option>
                            ))}
                          </select>
                        </div>

                        {optimizationTemplates?.[selectedTemplate] && (
                          <div className="text-sm">
                            <div className="text-[var(--text-secondary)] font-medium mb-2">Parameters to optimize:</div>
                            <ul className="text-[var(--text-muted)] space-y-1">
                              {Object.entries(optimizationTemplates[selectedTemplate].optimization_params).map(([param, values]) => (
                                <li key={param} className="text-xs">
                                  <code className="bg-[var(--vault-bg)] px-1 rounded">{param}</code>: {JSON.stringify(values)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <button
                          onClick={handleStartOptimization}
                          disabled={startOptimizationMutation.isLoading || !!runningOptimizationId}
                          className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-black bg-[var(--spotlight-gold)] hover:opacity-90 disabled:opacity-50"
                        >
                          {runningOptimizationId ? (
                            <>
                              <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                              Optimizing...
                            </>
                          ) : (
                            <>
                              <Cog6ToothIcon className="w-4 h-4 mr-2" />
                              Start Optimization
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Optimization Status */}
                    {(runningOptimizationId || optimizationResults) && (
                      <div className="mt-6 bg-[var(--vault-elevated)] rounded-lg p-4">
                        <h3 className="text-lg font-medium text-[var(--text-primary)] mb-4">Optimization Status</h3>
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(optimizationStatus?.status || optimizationResults?.status)}
                          <div>
                            <div className="text-sm font-medium text-[var(--text-primary)]">
                              {optimizationStatus?.status || optimizationResults?.status || 'Unknown'}
                            </div>
                            {optimizationStatus?.progress?.stage && (
                              <div className="text-xs text-[var(--text-muted)]">
                                {optimizationStatus.progress.stage}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Template Details */}
                  <div className="lg:col-span-2">
                    {optimizationTemplates?.[selectedTemplate] && (
                      <div className="bg-[var(--vault-elevated)] rounded-lg p-4">
                        <h3 className="text-lg font-medium text-[var(--text-primary)] mb-4">Template Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-2">Optimization Parameters</h4>
                            <pre className="text-xs bg-[var(--vault-bg)] rounded p-3 overflow-x-auto text-[var(--text-muted)]">
                              {JSON.stringify(optimizationTemplates[selectedTemplate].optimization_params, null, 2)}
                            </pre>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-[var(--text-secondary)] mb-2">Scoring Weights</h4>
                            <pre className="text-xs bg-[var(--vault-bg)] rounded p-3 overflow-x-auto text-[var(--text-muted)]">
                              {JSON.stringify(optimizationTemplates[selectedTemplate].scoring_weights, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Optimization Results */}
                {renderOptimizationResults()}
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                <h3 className="text-lg font-medium text-[var(--text-primary)] mb-4">Past Test Runs</h3>
                {historyLoading ? (
                  <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
                    <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                    Loading history...
                  </div>
                ) : !testHistory?.results?.length ? (
                  <div className="text-center py-12 text-[var(--text-muted)]">
                    No saved test results found. Run a test to generate results.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[var(--vault-border)]">
                          <th className="text-left py-3 px-4 text-[var(--text-muted)] font-medium">Date</th>
                          <th className="text-left py-3 px-4 text-[var(--text-muted)] font-medium">Type</th>
                          <th className="text-right py-3 px-4 text-[var(--text-muted)] font-medium">Tests</th>
                          <th className="text-right py-3 px-4 text-[var(--text-muted)] font-medium">Pass Rate</th>
                          <th className="text-right py-3 px-4 text-[var(--text-muted)] font-medium">Accuracy</th>
                          <th className="text-right py-3 px-4 text-[var(--text-muted)] font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {testHistory.results.map((entry) => {
                          const s = entry.summary;
                          const isClassification = entry.test_type === 'classification';
                          const total = isClassification ? s.total_tests : s.total_tests;
                          const passed = isClassification ? s.passed : s.target_reached;
                          const passRate = total > 0 ? ((passed / total) * 100).toFixed(1) : '—';
                          const accuracy = isClassification
                            ? (s.level_accuracy != null ? `${(s.level_accuracy * 100).toFixed(1)}%` : '—')
                            : (s.avg_preservation != null ? `${(s.avg_preservation * 100).toFixed(1)}% pres.` : '—');
                          const isLoading = loadingHistoryFile === entry.filename;

                          return (
                            <tr
                              key={entry.filename}
                              onClick={() => !isLoading && handleLoadHistoryResult(entry.filename)}
                              className="border-b border-[var(--vault-border)] hover:bg-[var(--vault-elevated)] cursor-pointer transition-colors"
                            >
                              <td className="py-3 px-4 text-[var(--text-primary)]">
                                {new Date(entry.timestamp).toLocaleString()}
                              </td>
                              <td className="py-3 px-4">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  isClassification
                                    ? 'bg-blue-900/30 text-blue-400'
                                    : 'bg-purple-900/30 text-purple-400'
                                }`}>
                                  {isClassification ? 'Classification' : 'Sanitization'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right text-[var(--text-secondary)]">{total}</td>
                              <td className="py-3 px-4 text-right">
                                <span className={passed === total ? 'text-green-400' : 'text-yellow-400'}>
                                  {passRate}%
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right text-[var(--text-secondary)]">{accuracy}</td>
                              <td className="py-3 px-4 text-right">
                                {isLoading ? (
                                  <ArrowPathIcon className="w-4 h-4 animate-spin text-[var(--text-muted)] inline" />
                                ) : (
                                  <span className="text-xs text-[var(--text-muted)]">View</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testing;